import { useCallback, useContext, useMemo, useState } from "react";
import { BasicUser } from "types/topicflow";

import CalibratedQuestionList from "@apps/assessments/components/calibration/calibrated-question-list";
import QuestionCalibrationSection from "@apps/assessments/components/calibration/question-calibration-section";
import useAssessmentCalibrationData from "@apps/assessments/components/calibration/use-assessment-calibration-data";
import { getQuestionLabels } from "@apps/assessments/components/questions/question";
import PerformanceAssessmentReportContext from "@apps/reporting/context/performance-assessment-report-context";
import useLabel from "@apps/use-label/use-label";
import Avatar from "@components/avatar/avatar";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Loading from "@components/loading/loading";
import Modal from "@components/modal/modal";
import MotionDiv from "@components/motion/motion-div";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { TopicflowIconMap } from "@components/topicflow-icon/topicflow-icon-helpers";

const CalibrationChangelogModal = ({
  answerIdByQuestionId,
  templateId,
  canCalibrate,
  canSuggestCalibration,
  target,
  reviewExists,
  onOpenEmployeeDrawer,
  onClose,
}: {
  templateId: number;
  canCalibrate: boolean;
  canSuggestCalibration: boolean;
  target: BasicUser;
  answerIdByQuestionId: Record<number, number>;
  reviewExists: boolean;
  onOpenEmployeeDrawer: () => void;
  onClose: () => void;
}) => {
  const label = useLabel();
  const [selectedChangelogQuestionId, setSelectedChangelogQuestionId] =
    useState<number | null>(null);
  const { complianceProgramId } = useContext(
    PerformanceAssessmentReportContext,
  );
  const { allCalibrations, questions, loading } = useAssessmentCalibrationData({
    complianceProgramId,
    templateId,
    targetId: target.id,
  });

  const {
    selectedQuestion,
    selectedQuestionLabels,
    selectedQuestionCalibrations,
  } = useMemo(() => {
    const question = questions.find(
      (question) => question.id === selectedChangelogQuestionId,
    );
    if (selectedChangelogQuestionId === null || !question) {
      return {
        selectedQuestion: null,
        selectedQuestionLabels: [],
      };
    }
    const calibrations = allCalibrations.filter(
      (calibration) => calibration.question?.id === selectedChangelogQuestionId,
    );
    const questionLabels =
      question.__typename === "RangeAssessmentQuestionNode" ||
      question.__typename === "TalentIndicatorAssessmentQuestionNode"
        ? getQuestionLabels(question)
        : {};
    return {
      selectedQuestion: question,
      selectedQuestionLabels: questionLabels,
      selectedQuestionCalibrations: calibrations,
    };
  }, [allCalibrations, questions, selectedChangelogQuestionId]);

  const handleOpenQuesitonInReview = useCallback(() => {
    onOpenEmployeeDrawer();
    onClose();
  }, [onClose, onOpenEmployeeDrawer]);

  return (
    <Modal onClose={onClose}>
      <div className="flex flex-col divide-y">
        <div className="p-6">
          <div className="flex justify-between gap-2">
            <div className="flex-1">
              <div className="tf-header-lg-dark">
                Calibration Comments or Edits
              </div>
              <div className="mt-1 flex items-center gap-2">
                <Avatar user={target} size="4" />
                <div className="text-gray-600">{target.name}</div>
              </div>
            </div>
            {onClose && (
              <Button
                icon={TopicflowIconMap.close}
                onClick={onClose}
                theme={buttonTheme.iconGray}
                aria-label="Close modal button"
              />
            )}
          </div>
        </div>
        {loading ? (
          <div className="p-6">
            <Loading />
          </div>
        ) : questions.length === 0 ? (
          <div className="p-6">No change log.</div>
        ) : selectedQuestion ? (
          <MotionDiv
            className="p-6"
            key="question-calibration-section"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <Button
                size={ButtonSize.medium}
                className="flex items-center gap-2"
                onClick={() => setSelectedChangelogQuestionId(null)}
              >
                <TopicflowIcon type="arrowLeft" className="h-3 w-3" />
                Back to Summary
              </Button>
              {reviewExists && (
                <Button
                  size={ButtonSize.medium}
                  theme={buttonTheme.lightBlue}
                  onClick={handleOpenQuesitonInReview}
                >
                  {`View Question in ${label("review", { capitalize: true })}`}
                </Button>
              )}
            </div>
            <QuestionCalibrationSection
              complianceProgramId={complianceProgramId}
              templateId={templateId}
              questionId={selectedQuestion.id}
              answerId={answerIdByQuestionId[selectedQuestion.id]}
              targetId={target.id}
              calibrations={selectedQuestionCalibrations}
              canCalibrate={canCalibrate}
              canSuggestCalibration={canSuggestCalibration}
              expanded
              questionLabels={selectedQuestionLabels}
              title={selectedQuestion.title}
              hideCollapseButton
            />
          </MotionDiv>
        ) : (
          <MotionDiv
            className="p-4"
            key="quesiton-list"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <CalibratedQuestionList
              allCalibrations={allCalibrations}
              questions={questions}
              onSelectQuestion={setSelectedChangelogQuestionId}
            />
          </MotionDiv>
        )}
      </div>
    </Modal>
  );
};

export default CalibrationChangelogModal;
